/**
 * The theme toggle.
 *
 * Three states, not two: Light / Dark / System. A two-state toggle gives no
 * way back to following the OS once it has been touched, which is exactly
 * the "stuck in the wrong state" failure a phone-style toggle is supposed
 * to avoid.
 *
 * On a phone it collapses to a single button that cycles the three states,
 * because a segmented control at that size is a row of mis-taps.
 */

import { Monitor, Moon, Sun } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useThemeControls } from '@/theme/ThemeProvider';
import type { ThemeChoice } from '@/theme/tokens';

const OPTIONS: ReadonlyArray<{ value: ThemeChoice; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

export function ThemeToggle({ className }: { className?: string }) {
  const { choice, setChoice } = useThemeControls();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-px rounded border border-border bg-surface-2 p-px',
        className,
      )}
    >
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const active = option.value === choice;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={option.label}
            title={
              option.value === 'system'
                ? 'Follow the operating system'
                : `${option.label} theme`
            }
            onClick={() => setChoice(option.value)}
            className={cn(
              'inline-flex size-6 items-center justify-center rounded-[3px] transition-colors',
              active ? 'bg-accent text-accent-fg' : 'text-mid hover:bg-surface-3 hover:text-hi',
            )}
          >
            <Icon size={13} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

/** Single-button variant that cycles light → dark → system. For mobile. */
export function ThemeToggleCompact({ className }: { className?: string }) {
  const { choice, theme, setChoice } = useThemeControls();
  const Icon = choice === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;
  const next: ThemeChoice =
    choice === 'light' ? 'dark' : choice === 'dark' ? 'system' : 'light';

  return (
    <button
      type="button"
      onClick={() => setChoice(next)}
      aria-label={`Theme: ${choice}. Switch to ${next}.`}
      title={`Theme: ${choice} — tap for ${next}`}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded border border-border',
        'bg-surface-2 text-mid transition-colors hover:text-hi',
        className,
      )}
    >
      <Icon size={14} aria-hidden />
    </button>
  );
}
